import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, FormControl, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Normalize language code to handle regional variants (e.g., en-US -> en)
  const normalizeLanguageCode = (lang: string): string => {
    if (!lang) return 'en';
    const normalized = lang.split('-')[0]; // Take only the language part before the dash
    return ['en', 'es'].includes(normalized) ? normalized : 'en';
  };

  // Update language state when i18n is ready
  useEffect(() => {
    const updateLanguage = () => {
      const lang = normalizeLanguageCode(i18n.language);
      setCurrentLanguage(lang);
    };

    // Set initial language
    updateLanguage();

    // Listen for language changes
    i18n.on('languageChanged', updateLanguage);

    return () => {
      i18n.off('languageChanged', updateLanguage);
    };
  }, [i18n]);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    // Store preference
    localStorage.setItem('preferredLanguage', languageCode);
  };

  return (
    <Box sx={{ minWidth: 120 }}>
      <FormControl size="small" fullWidth>
        <Select
          value={currentLanguage}
          onChange={(e) => handleLanguageChange(e.target.value)}
          displayEmpty
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '0.875rem',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          {languages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{lang.flag}</span>
                <span style={{ color: 'black' }}>{lang.name}</span>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default LanguageSwitcher;